export const normalizeClockValue = (timeValue = '') => {
  const match = String(timeValue).trim().match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
  if (!match) return String(timeValue).trim();

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.toUpperCase();

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(timeValue).trim();

  if (meridiem) {
    if (hours === 12) hours = 0;
    if (meridiem === 'PM') hours += 12;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const timeToMinutes = (timeValue = '') => {
  const [hours, minutes] = normalizeClockValue(timeValue).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
};

export const addMinutesToClock = (timeValue = '09:00', minutesToAdd = 30) => {
  const total = timeToMinutes(timeValue);
  if (total === null) return '09:30';

  const next = (total + minutesToAdd + (24 * 60)) % (24 * 60);
  const hours = Math.floor(next / 60);
  const minutes = next % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const getDefaultPriorityRange = (index) => ({
  start_time: '',
  end_time: '',
});

export const getInitialPriorityTouchMap = (priorities = []) => {
  const touchMap = {};

  (priorities || []).forEach((priority, index) => {
    const defaults = getDefaultPriorityRange(index);
    touchMap[index] = Boolean(
      priority?.blocked_event_id
      || priority?.start_time !== defaults.start_time
      || priority?.end_time !== defaults.end_time
    );
  });

  return touchMap;
};

export const validatePriorityRanges = (priorities = []) => {
  const activePriorities = (priorities || []).map((priority, originalIndex) => ({ priority, originalIndex })).filter(({ priority }) => {
    const hasTitle = Boolean(priority?.title?.trim());
    const hasGoalNiyyah = Boolean(priority?.goal_niyyah?.trim());
    const hasStart = timeToMinutes(priority?.start_time) !== null;
    const hasEnd = timeToMinutes(priority?.end_time) !== null;
    return hasTitle || hasGoalNiyyah || hasStart || hasEnd;
  });

  const invalidRanges = [];
  const sequenceConflicts = [];

  activePriorities.forEach(({ priority, originalIndex }, index) => {
    const startMinutes = timeToMinutes(priority?.start_time);
    const endMinutes = timeToMinutes(priority?.end_time);
    const hasTimeValue = startMinutes !== null || endMinutes !== null;
    const hasFullRange = startMinutes !== null && endMinutes !== null;

    if (!hasTimeValue) {
      return;
    }

    if (!hasFullRange || startMinutes >= endMinutes) {
      invalidRanges.push({ index: originalIndex, priority });
      return;
    }

    if (index === 0) return;

    const previousEntry = activePriorities[index - 1];
    const previousEndMinutes = timeToMinutes(previousEntry.priority?.end_time);
    if (previousEndMinutes !== null && startMinutes < previousEndMinutes) {
      sequenceConflicts.push({
        previousIndex: previousEntry.originalIndex,
        currentIndex: originalIndex,
        previousPriority: previousEntry.priority,
        currentPriority: priority,
      });
    }
  });

  return {
    invalidRanges,
    sequenceConflicts,
    hasInvalidRanges: invalidRanges.length > 0,
    hasSequenceConflicts: sequenceConflicts.length > 0,
  };
};

export const getPriorityWarningMap = (priorities = []) => {
  const validation = validatePriorityRanges(priorities);
  const warningMap = {};

  validation.invalidRanges.forEach(({ index }) => {
    warningMap[index] = {
      ...(warningMap[index] || {}),
      invalidRange: true,
    };
  });

  validation.sequenceConflicts.forEach(({ previousIndex, currentIndex }) => {
    warningMap[previousIndex] = {
      ...(warningMap[previousIndex] || {}),
      sequenceConflict: true,
    };
    warningMap[currentIndex] = {
      ...(warningMap[currentIndex] || {}),
      sequenceConflict: true,
    };
  });

  return warningMap;
};